import SafetyCertificateOutlined from "@ant-design/icons/SafetyCertificateOutlined";
import { MCP_FEATURE_FLAG } from "@shared/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { sharedLib } from "@shared/services/shared.service";
import {
  LanguageNameSpaces,
  OAuthAuthorizationDecision,
  OAuthAuthorizeResponse,
} from "@shared/types";
import Button from "common/Buttons/Button";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Text from "common/Typography/Text";
import Title from "common/Typography/Title";
import cornixLogo from "images/svg_logo.svg";
import cornixWhiteLogo from "images/svg_white_logo.svg";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { TriangleAlert } from "lucide-react";
import NewLookAuthLayout from "features/auth/newLook/NewLookAuthLayout";
import { PrimaryTitle } from "features/auth/styles";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens";
import { openMessage } from "utils/misc";

type Consent = Required<
  Pick<OAuthAuthorizeResponse, "client_name" | "redirect_uri" | "scope">
>;

const OAuthAuthorizeLoginPage = () => {
  const [oAuthAuthorize, { isLoading, originalArgs }] =
    sharedLib.sharedApi.authentication.useOauthAuthorizeMutation();
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);
  const hasMcpAccess = user?.hasFeatureFlag(MCP_FEATURE_FLAG);
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const [consent, setConsent] = useState<Consent>();
  const invalidLinkError = t("oauth_authorization.errors.invalid_link");
  const invalidRequestError = t("oauth_authorization.errors.invalid_request");
  const authorizeUrl = useMemo(
    () => getAuthorizeUrl(new URLSearchParams(location.search).get("next")),
    [location.search]
  );

  useEffect(() => {
    if (!user) return;
    if (!authorizeUrl) {
      openMessage(invalidLinkError, "error");
      history.replace("/");
      return;
    }
    oAuthAuthorize({ url: authorizeUrl.toString() })
      .unwrap()
      .then((data) => {
        if (data.redirect_url) {
          window.location.replace(data.redirect_url);
          return;
        }
        if (!hasMcpAccess || !isConsent(data)) throw new Error();
        setConsent(data);
      })
      .catch(() => {
        openMessage(invalidRequestError, "error");
        history.replace("/");
      });
  }, [
    authorizeUrl,
    hasMcpAccess,
    history,
    invalidLinkError,
    invalidRequestError,
    oAuthAuthorize,
    user,
  ]);

  const submitDecision = async (decision: OAuthAuthorizationDecision) => {
    if (!authorizeUrl) return;
    try {
      const data = await oAuthAuthorize({
        url: authorizeUrl.toString(),
        decision,
      }).unwrap();
      if (!data.redirect_url) throw new Error();
      window.location.replace(data.redirect_url);
    } catch {
      openMessage(t("oauth_authorization.errors.failed"), "error");
    }
  };

  if (!consent) return <LoadingSpinnerOverlay isLoading spinnerSize="large" />;
  const destination = new URL(consent.redirect_uri).host;

  if (theme.isNewLook) {
    return (
      <NewLookAuthLayout>
        <PrimaryTitle $isMobile={false}>
          {t("oauth_authorization.title", {
            clientName: consent.client_name,
          })}
        </PrimaryTitle>
        <NlLabel>{t("oauth_authorization.requested_access")}</NlLabel>
        <NlAccess>
          <div>
            <NlAccessTitle>
              {t("oauth_authorization.account_access")}
              <NlScope>{consent.scope}</NlScope>
            </NlAccessTitle>
            <NlAccessDescription>
              {t("oauth_authorization.account_access_description")}
            </NlAccessDescription>
          </div>
        </NlAccess>
        <NlDestination>
          <NlCallback>{consent.redirect_uri}</NlCallback>
          <NlWarning>
            <TriangleAlert size={16} strokeWidth={1.75} aria-hidden />
            <span>
              {t("oauth_authorization.redirect_warning", { destination })}
            </span>
          </NlWarning>
        </NlDestination>
        <NlActions>
          <Button
            $size="l"
            disabled={isLoading}
            loading={isLoading && originalArgs?.decision === "deny"}
            onClick={() => submitDecision("deny")}
          >
            {t("oauth_authorization.cancel")}
          </Button>
          <Button
            type="primary"
            $size="l"
            disabled={isLoading}
            loading={isLoading && originalArgs?.decision === "approve"}
            onClick={() => submitDecision("approve")}
          >
            {t("oauth_authorization.approve")}
          </Button>
        </NlActions>
      </NewLookAuthLayout>
    );
  }

  return (
    <Page>
      <Logo
        src={theme.isDarkMode ? cornixWhiteLogo : cornixLogo}
        alt="Cornix"
      />
      <ConsentPanel>
        <PageTitle>
          {t("oauth_authorization.title", {
            clientName: consent.client_name,
          })}
        </PageTitle>
        <Content>
          <AccessList>
            <SectionLabel>
              {t("oauth_authorization.requested_access")}
            </SectionLabel>
            <AccessItem>
              <AccessIcon />
              <AccessCopy>
                <AccessTitle>
                  {t("oauth_authorization.account_access")}
                </AccessTitle>
                <AccessDescription>
                  {t("oauth_authorization.account_access_description")}
                </AccessDescription>
              </AccessCopy>
              <Scope>{consent.scope}</Scope>
            </AccessItem>
          </AccessList>
          <Approval>
            <Destination>
              <Callback>{consent.redirect_uri}</Callback>
              <Warning>
                {t("oauth_authorization.redirect_warning", { destination })}
              </Warning>
            </Destination>
            <Actions>
              <ActionButton
                disabled={isLoading}
                loading={isLoading && originalArgs?.decision === "deny"}
                onClick={() => submitDecision("deny")}
              >
                {t("oauth_authorization.cancel")}
              </ActionButton>
              <ActionButton
                type="primary"
                disabled={isLoading}
                loading={isLoading && originalArgs?.decision === "approve"}
                onClick={() => submitDecision("approve")}
              >
                {t("oauth_authorization.approve")}
              </ActionButton>
            </Actions>
          </Approval>
        </Content>
      </ConsentPanel>
    </Page>
  );
};

export default OAuthAuthorizeLoginPage;

const getAuthorizeUrl = (nextUrl: string | null) => {
  if (!nextUrl) return;
  try {
    const url = new URL(nextUrl, window.location.origin);
    return url.origin === window.location.origin &&
      url.pathname.endsWith("/oauth/authorize/")
      ? url
      : undefined;
  } catch {
    return undefined;
  }
};

const isConsent = (response: OAuthAuthorizeResponse): response is Consent =>
  typeof response.client_name === "string" &&
  typeof response.redirect_uri === "string" &&
  typeof response.scope === "string";

const Page = styled.main`
  min-height: 100vh;
  padding: 32px 40px;
  background: ${(p) => p.theme.colors.layoutBgColor};
  color: ${(p) => p.theme.colors.textColor};
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 720px) {
    padding: 24px 16px;
  }
`;
const Logo = styled.img`
  width: 116px;
  height: 56px;
  object-fit: contain;
  margin-bottom: 24px;
`;
const ConsentPanel = styled.section`
  width: min(1060px, 100%);
  padding: 44px 48px 48px;
  background: ${(p) => p.theme.colors.cardBgColor};
  border: 1px solid ${(p) => p.theme.colors.mediumBorderGrey};
  border-radius: 8px;
  box-shadow: 0 10px 24px ${(p) => p.theme.colors.lightestBorderGrey};
  @media (max-width: 720px) {
    padding: 28px 20px;
  }
`;
const PageTitle = styled(Title).attrs({ level: 2 })`
  margin: 0 0 36px;
  text-align: center;
  font-size: 22px;
  line-height: 1.4;
  font-weight: 400;
  letter-spacing: 0;
`;
const Content = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 1fr);
  gap: 48px;
  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;
const AccessList = styled.div`
  min-width: 0;
`;
const SectionLabel = styled(Text).attrs({ as: "div", type: "secondary" })`
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
`;
const AccessItem = styled.div`
  min-height: 112px;
  padding: 20px;
  border-left: 3px solid ${(p) => p.theme.colors.primaryBlue};
  background: ${(p) => p.theme.colors.secondaryCardBgColor};
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: start;
`;
const AccessIcon = styled(SafetyCertificateOutlined)`
  margin-top: 2px;
  color: ${(p) => p.theme.colors.primaryBlue};
  font-size: 20px;
`;
const AccessCopy = styled.div`
  min-width: 0;
`;
const AccessTitle = styled(Title).attrs({ level: 4 })`
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
`;
const AccessDescription = styled(Text).attrs({
  as: "div",
  type: "secondary",
})`
  font-size: 14px;
  line-height: 1.55;
`;
const Scope = styled(Text).attrs({ as: "span" })`
  padding: 4px 9px;
  border-radius: 4px;
  background: ${(p) => p.theme.colors.lightBlue};
  color: ${(p) => p.theme.colors.primaryBlue};
  font-size: 12px;
  font-weight: 600;
`;
const Approval = styled.div`
  min-width: 0;
`;
const Destination = styled.div`
  padding: 16px;
  border: 1px solid ${(p) => p.theme.colors.orange};
  border-radius: 8px;
  background: ${(p) => p.theme.colors.highlightBgColor};
`;
const Callback = styled(Text).attrs({ as: "code" })`
  display: block;
  padding: 10px;
  overflow-wrap: anywhere;
  background: ${(p) => p.theme.colors.layoutBgColor};
  border-radius: 4px;
  font-size: 13px;
`;
const Warning = styled(Text)`
  margin: 12px 0 0;
  color: ${(p) => p.theme.colors.orange};
  font-size: 14px;
  line-height: 1.55;
`;
const Actions = styled.div`
  margin-top: 28px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;
const ActionButton = styled(Button)`
  min-width: 112px;
  height: 44px;
  @media (max-width: 480px) {
    min-width: 0;
    flex: 1;
  }
`;

// New look (consent inside the sign-in layout): label, a well with the
// requested access, the destination with an orange warning, two actions.
const NlLabel = styled.div`
  margin-bottom: 8px;
  ${newLookType.label};
  color: ${(p) => p.theme.colors.secondaryText};
`;
const NlAccess = styled.div`
  padding: 12px 16px;
  border-radius: ${(p) => p.theme.radii.control}px;
  border: 1px solid ${(p) => p.theme.colors.cardBorderColor};
  background: ${(p) => getNewLookTokens(p.theme).well};
`;
const NlAccessTitle = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  ${newLookType.panelTitle};
  color: ${(p) => p.theme.colors.titleColor};
`;
const NlScope = styled.span`
  padding: 0 6px;
  border-radius: 6px;
  border: 1px solid ${(p) => p.theme.colors.cardBorderColor};
  background: ${(p) => p.theme.colors.cardBgColor};
  ${newLookType.meta};
  color: ${(p) => p.theme.colors.secondaryText};
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;
const NlAccessDescription = styled.div`
  margin-top: 4px;
  ${newLookType.body};
  color: ${(p) => p.theme.colors.secondaryText};
`;
const NlDestination = styled.div`
  margin-top: 16px;
`;
const NlCallback = styled.code`
  display: block;
  padding: 8px 12px;
  border-radius: ${(p) => p.theme.radii.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  border: 1px solid ${(p) => p.theme.colors.cardBorderColor};
  color: ${(p) => p.theme.colors.primaryText};
  ${newLookType.meta};
  overflow-wrap: anywhere;
`;
const NlWarning = styled.div`
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  gap: 8px;
  margin-top: 12px;
  ${newLookType.body};
  color: ${(p) => p.theme.colors.primaryText};
  svg {
    margin-top: 2px;
    color: ${(p) => getNewLookTokens(p.theme).stop};
  }
`;
const NlActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 24px;
`;
